import * as React from "react";
import styled from "styled-components";
import { useSpring, animated } from "react-spring";
import { Link } from "../../shared/Link";
import { BREAKPOINTS } from "../../../helpers/theme";
import { toREM, toVWDesktop } from "../../../helpers/styles";
import * as CommonHeaderTypes from "../types";

const TOP_MENU_SELECTOR = "#theNavBar .menu-item";
const DRAWER_LEFT_PANEL_SELECTOR =
  '#menu-desktop-large-drawer [data-drawer-left-panel="true"] a[href], #menu-desktop-small-drawer [data-drawer-left-panel="true"] a[href]';
const DRAWER_ALL_LINKS_SELECTOR =
  "#menu-desktop-large-drawer a[href], #menu-desktop-small-drawer a[href]";

export type MenuItemProps = {
  className?: string;
  onClick: (mItem: CommonHeaderTypes.MenuItem) => void;
  onMouseEnter?: (mItem: CommonHeaderTypes.MenuItem) => void;
  onMouseLeave?: (mItem: CommonHeaderTypes.MenuItem) => void;
  menuItem: CommonHeaderTypes.MenuItem;
  active: boolean;
  fontColor: string;
};

const MenuItem = ({
  className,
  onClick,
  onMouseEnter,
  onMouseLeave,
  active,
  menuItem,
  fontColor,
}: MenuItemProps) => {
  const hasLink = "link" in menuItem && !!menuItem.link;
  const hasSubMenu =
    "subMenuItems" in menuItem &&
    Array.isArray(menuItem.subMenuItems) &&
    menuItem.subMenuItems.length > 0;

  const styles = useSpring({ width: active ? "100%" : "0%" });

  const getElements = (selector: string) =>
    Array.from(document.querySelectorAll<HTMLElement>(selector));

  const openMenuItem = () => {
    onMouseEnter?.(menuItem);
  };

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!hasLink) {
      event.preventDefault();
    }
    onClick(menuItem);
  };

  const focusDrawerItem = (selector: string, position: "first" | "last") => {
    openMenuItem();

    window.requestAnimationFrame(() => {
      const drawerItems = getElements(selector);
      const focusableDrawerItems =
        drawerItems.length > 0
          ? drawerItems
          : getElements(DRAWER_ALL_LINKS_SELECTOR);
      const drawerItem =
        position === "first"
          ? focusableDrawerItems[0]
          : focusableDrawerItems[focusableDrawerItems.length - 1];

      drawerItem?.focus();
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    const topMenuItems = getElements(TOP_MENU_SELECTOR);
    const currentIndex = topMenuItems.indexOf(event.currentTarget);

    const focusTopMenuItemAtIndex = (index: number) => {
      topMenuItems[index]?.focus();
    };

    if (event.key === "ArrowRight" && topMenuItems.length > 0) {
      event.preventDefault();
      focusTopMenuItemAtIndex((currentIndex + 1) % topMenuItems.length);
      return;
    }

    if (event.key === "ArrowLeft" && topMenuItems.length > 0) {
      event.preventDefault();
      focusTopMenuItemAtIndex(
        (currentIndex - 1 + topMenuItems.length) % topMenuItems.length,
      );
      return;
    }

    if (event.key === "Home" && topMenuItems.length > 0) {
      event.preventDefault();
      focusTopMenuItemAtIndex(0);
      return;
    }

    if (event.key === "End" && topMenuItems.length > 0) {
      event.preventDefault();
      focusTopMenuItemAtIndex(topMenuItems.length - 1);
      return;
    }

    if (event.key === "ArrowDown" && hasSubMenu) {
      event.preventDefault();
      focusDrawerItem(DRAWER_LEFT_PANEL_SELECTOR, "first");
      return;
    }

    if (event.key === "ArrowUp" && hasSubMenu) {
      event.preventDefault();
      focusDrawerItem(DRAWER_LEFT_PANEL_SELECTOR, "last");
      return;
    }

    if (event.key === "Tab" && !event.shiftKey && hasSubMenu) {
      event.preventDefault();
      openMenuItem();

      window.requestAnimationFrame(() => {
        const firstDrawerItem = getElements(DRAWER_ALL_LINKS_SELECTOR)[0];
        firstDrawerItem?.focus();
      });
    }
  };

  return (
    <StyledLink
      className={`${className} menu-item`}
      active={active}
      fontColor={fontColor}
      onClick={handleClick}
      onMouseEnter={openMenuItem}
      onMouseLeave={() => onMouseLeave?.(menuItem)}
      onFocus={openMenuItem}
      onKeyDown={handleKeyDown}
      role={!hasLink ? "button" : undefined}
      aria-haspopup={hasSubMenu ? "menu" : undefined}
      aria-expanded={hasSubMenu ? active : undefined}
      aria-controls={hasSubMenu ? "menu-desktop-large-drawer" : undefined}
      to={hasLink ? menuItem.link : "#"}
    >
      {menuItem.label}
      <Bar style={styles} className={"menu-bar"} />
    </StyledLink>
  );
};

const StyledLink = styled(Link)<{ fontColor: string; active: boolean }>`
  display: none;
  background: inherit;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    font-family: ${(props) => props.theme.fontFamily},
      ${(props) => props.theme.fallBackFontFamily}, sans-serif;
    font-weight: ${(props) => (props.active ? 900 : 800)};
    font-size: ${toREM(14)}rem;
    line-height: ${toREM(18)}rem;
    letter-spacing: 0.045em;
    color: ${(props) => props.fontColor};
    text-transform: uppercase;
    margin: ${(props) =>
      props.theme.isRTL
        ? `0 ${toVWDesktop(38)}vw 0 0`
        : `0 0 0 ${toVWDesktop(38)}vw`};
    text-decoration: none;
  }
`;

const Bar = styled(animated.div)`
  width: 0;
  height: 3px;
  background-color: var(--color-alt-green-03);
`;

export default MenuItem;
